/**
 * TierGroupPicker — search Entra ID for a security group or create one.
 * Used in the settings page for configuring tier group object IDs.
 */

import { useState, useRef, useEffect } from "react";
import { Search, Plus, Check, X, Loader2, AlertTriangle } from "lucide-react";
import {
  useEntraGroupSearch,
  useEntraGroup,
  useCreateEntraGroup,
  type EntraGroup,
} from "@/hooks/use-entra-groups";

interface TierGroupPickerProps {
  readonly tier: string;
  readonly tierLabel: string;
  readonly description: string;
  readonly value: string;
  readonly savedName: string;
  readonly onChange: (groupId: string, groupName?: string) => void;
}

export function TierGroupPicker({
  tier,
  tierLabel,
  description,
  value,
  savedName,
  onChange,
}: TierGroupPickerProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const searchResult = useEntraGroupSearch(searchQuery);
  const groupInfo = useEntraGroup(value);
  const createMutation = useCreateEntraGroup();

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsSearchOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function handleSelect(group: EntraGroup) {
    onChange(group.id, group.displayName);
    setIsSearchOpen(false);
    setSearchQuery("");
  }

  function handleCreate() {
    createMutation.mutate(tier, {
      onSuccess: (group) => {
        onChange(group.id, group.displayName);
        setIsSearchOpen(false);
        setSearchQuery("");
      },
    });
  }

  function handleClear() {
    onChange("", "");
    setSearchQuery("");
  }

  // Use Graph-resolved name if available, fall back to saved name
  const displayName = groupInfo.data?.displayName ?? savedName;
  const isRoleAssignable = groupInfo.data?.isAssignableToRole;

  return (
    <div className="space-y-1">
      <label className="text-label text-sc-text-muted uppercase tracking-wider block">
        {tierLabel}
        <span className="normal-case tracking-normal ml-2 text-sc-text-dim">
          {description}
        </span>
      </label>

      {/* Current value display */}
      {value && !manualMode ? (
        <div className="flex items-center gap-2 h-9 w-full rounded-input border border-sc-border bg-sc-input-bg px-3">
          <Check size={14} className="text-sc-primary shrink-0" />
          <span className="text-body-sm text-sc-text truncate flex-1 font-mono">
            {displayName ? (
              <>
                <span className="font-sans font-medium">{displayName}</span>
                <span className="text-sc-text-muted ml-2 text-xs">{value}</span>
              </>
            ) : (
              value
            )}
          </span>
          {value && isRoleAssignable === false && (
            <span title="Group is not role-assignable — PIM will not work">
              <AlertTriangle size={14} className="text-sc-warning shrink-0" />
            </span>
          )}
          <button
            type="button"
            onClick={handleClear}
            className="text-sc-text-muted hover:text-sc-danger shrink-0"
          >
            <X size={14} />
          </button>
        </div>
      ) : manualMode ? (
        /* Manual GUID entry */
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value.trim())}
            placeholder="00000000-0000-0000-0000-000000000000"
            className="h-9 flex-1 rounded-input border border-sc-border bg-sc-input-bg px-3 text-body-sm text-sc-text font-mono"
          />
          <button
            type="button"
            onClick={() => setManualMode(false)}
            className="text-label text-sc-primary hover:underline shrink-0"
          >
            Search
          </button>
        </div>
      ) : (
        /* Search mode */
        <div className="relative" ref={dropdownRef}>
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-sc-text-muted"
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setIsSearchOpen(true);
                }}
                onFocus={() => searchQuery.length >= 2 && setIsSearchOpen(true)}
                placeholder={`Search Entra groups (e.g. "${tierLabel}")...`}
                className="h-9 w-full rounded-input border border-sc-border bg-sc-input-bg pl-9 pr-3 text-body-sm text-sc-text"
              />
              {searchResult.isFetching && (
                <Loader2
                  size={14}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-sc-text-muted animate-spin"
                />
              )}
            </div>
            <button
              type="button"
              onClick={() => setManualMode(true)}
              className="text-label text-sc-text-muted hover:text-sc-text shrink-0 whitespace-nowrap"
            >
              Enter ID
            </button>
          </div>

          {/* Dropdown results */}
          {isSearchOpen && searchQuery.length >= 2 && (
            <div className="absolute z-50 mt-1 w-full rounded-card border border-sc-border bg-white dark:bg-gray-900 shadow-lg max-h-64 overflow-auto">
              {searchResult.data?.items.length === 0 && !searchResult.isFetching && (
                <div className="px-3 py-2 text-body-sm text-sc-text-muted">
                  No groups found
                </div>
              )}

              {searchResult.data?.items.map((group) => (
                <button
                  key={group.id}
                  type="button"
                  onClick={() => handleSelect(group)}
                  className="w-full text-left px-3 py-2 hover:bg-sc-surface-hover transition-colors border-b border-sc-border last:border-b-0"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-body-sm text-sc-text font-medium truncate">
                      {group.displayName}
                    </span>
                    {group.isAssignableToRole ? (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-sc-primary/10 text-sc-primary shrink-0">
                        Role-assignable
                      </span>
                    ) : (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-sc-warning/10 text-sc-warning shrink-0">
                        Not role-assignable
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-sc-text-muted font-mono mt-0.5">
                    {group.id}
                  </div>
                </button>
              ))}

              {/* Create button */}
              <button
                type="button"
                onClick={handleCreate}
                disabled={createMutation.isPending}
                className="w-full text-left px-3 py-2 hover:bg-sc-surface-hover transition-colors flex items-center gap-2 border-t border-sc-border"
              >
                {createMutation.isPending ? (
                  <Loader2 size={14} className="text-sc-primary animate-spin" />
                ) : (
                  <Plus size={14} className="text-sc-primary" />
                )}
                <span className="text-body-sm text-sc-primary font-medium">
                  {createMutation.isPending
                    ? `Creating ${tierLabel}...`
                    : `Create "${tierLabel}" group`}
                </span>
              </button>

              {createMutation.isError && (
                <div className="px-3 py-2 text-label text-sc-danger">
                  {createMutation.error instanceof Error
                    ? createMutation.error.message
                    : "Failed to create group"}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
